import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import Layout from '@/components/layout/Layout'
import HomePage from '@/pages/public/HomePage'
import LoginPage from '@/pages/auth/LoginPage'
import VerifyPage from '@/pages/auth/VerifyPage'
import OAuthCallbackPage from '@/pages/auth/OAuthCallbackPage'
import ExpertDashboard from '@/pages/expert/Dashboard'
import CompanyDashboard from '@/pages/company/Dashboard'
import ProfilePage from '@/pages/ProfilePage'
import ExpertsListPage from '@/pages/public/ExpertsListPage'
import ExpertPublicPage from '@/pages/public/ExpertPublicPage'
import NotFoundPage from '@/pages/NotFoundPage'
import { AssessmentsListPage } from '@/pages/expert/AssessmentsListPage'
import { AssessmentPage } from '@/pages/expert/AssessmentPage'
import { JobBoardPage } from '@/pages/public/JobBoardPage'
import { JobDetailPage } from '@/pages/public/JobDetailPage'
import { ApplyJobPage } from '@/pages/expert/ApplyJobPage'
import { ApplicationsPage } from '@/pages/expert/ApplicationsPage'
import { SavedJobsPage } from '@/pages/expert/SavedJobsPage'
import { CompanyJobsPage } from '@/pages/company/JobsPage'
import { PostJobPage } from '@/pages/company/PostJobPage'
import { JobApplicationsPage } from '@/pages/company/JobApplicationsPage'
import EarningsPage from '@/pages/expert/EarningsPage'
import ContractsPage from '@/pages/expert/ContractsPage'
import CompanyContractsPage from '@/pages/company/ContractsPage'
import PaymentsPage from '@/pages/company/PaymentsPage'

// Protected route wrapper
function ProtectedRoute({ children, allowedTypes }: { children: React.ReactNode; allowedTypes?: string[] }) {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (allowedTypes && !allowedTypes.includes(user.user_type)) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function App() {
  const { user } = useAuth()

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route path="experts" element={<ExpertsListPage />} />
        <Route path="experts/:id" element={<ExpertPublicPage />} />
        <Route path="jobs" element={<JobBoardPage />} />
        <Route path="jobs/:id" element={<JobDetailPage />} />
      </Route>

      {/* Auth routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/verify" element={<VerifyPage />} />
      <Route path="/auth/callback" element={<OAuthCallbackPage />} />

      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={
          user?.user_type === 'expert' ? <ExpertDashboard /> : <CompanyDashboard />
        } />
        <Route path="profile" element={<ProfilePage />} />
      </Route>

      {/* Expert-only routes */}
      <Route path="/expert" element={
        <ProtectedRoute allowedTypes={['expert']}>
          <Layout />
        </ProtectedRoute>
      }>
        <Route path="assessments" element={<AssessmentsListPage />} />
        <Route path="assessments/new" element={<AssessmentPage />} />
        <Route path="assessments/:id" element={<AssessmentPage />} />
        <Route path="applications" element={<ApplicationsPage />} />
        <Route path="contracts" element={<ContractsPage />} />
        <Route path="contracts/:id" element={<ContractsPage />} />
        <Route path="earnings" element={<EarningsPage />} />
        <Route path="payments" element={<EarningsPage />} />
        <Route path="payments/refresh" element={<EarningsPage />} />
        <Route path="saved-jobs" element={<SavedJobsPage />} />
      </Route>

      {/* Expert apply route (protected, with layout) */}
      <Route element={
        <ProtectedRoute allowedTypes={['expert']}>
          <Layout />
        </ProtectedRoute>
      }>
        <Route path="/jobs/:id/apply" element={<ApplyJobPage />} />
      </Route>

      {/* Company-only routes */}
      <Route path="/company" element={
        <ProtectedRoute allowedTypes={['company']}>
          <Layout />
        </ProtectedRoute>
      }>
        <Route path="jobs" element={<CompanyJobsPage />} />
        <Route path="jobs/new" element={<PostJobPage />} />
        <Route path="jobs/:id" element={<PostJobPage />} />
        <Route path="jobs/:id/edit" element={<PostJobPage />} />
        <Route path="jobs/:id/applications" element={<JobApplicationsPage />} />
        <Route path="candidates" element={<div>Candidates Page</div>} />
        <Route path="contracts" element={<CompanyContractsPage />} />
        <Route path="contracts/:id" element={<CompanyContractsPage />} />
        <Route path="payments" element={<PaymentsPage />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}

export default App
